import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Step,
  StepLabel,
  Stepper,
  Switch,
} from '@mui/material';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import Colours from '../../../design-system/colours';
import breakpoints from '../../../design-system/breakpoints';
import { Field, FieldProps, FormikProvider, useFormik } from 'formik';
import { CreatePhysicalProductRequest } from '../../../common/model/dto/physical-product/create-physical-product-request';
import OvTextField from '../atoms/OvTextField';
import OvQuillEditor from '../atoms/OvQuillEditor';
import OvImageUpload from './OvImageUpload';
import {
  PRODUCT_IMAGES_PATH,
  PRODUCT_THUMBNAIL_IMAGES_PATH,
} from '../../../api/firebase-storage';
import OvButton from '../atoms/OvButton';
import Variables from '../../../design-system/variables';
import OvProductPlatformSelector from '../atoms/OvProductPlatformSelector';
import OvProductTypeSelector from '../atoms/OvProductTypeSelector';
import Stripe from 'stripe';
import OvSelect from '../atoms/OvSelect';
import { PhysicalProductPriceInterval } from '../../../common/model/dto/physical-product/physical-product-price-interval';
import * as Yup from 'yup';
import { isEmpty } from 'lodash';
import { formatPrice } from '../../../common/utils/helpers';
import { getCountriesSelectOptions } from '../../../common/model/dto/shipping-rate/countries-select-options';

interface OvCreatePhysicalProductDialogProps {
  isOpen: boolean;
  onCancel: () => void;
  onSave: (request: any) => void;
}

const OvCreatePhysicalProductDialog: FC<OvCreatePhysicalProductDialogProps> = ({
  isOpen,
  onCancel,
  onSave,
}) => {
  const { t } = useTranslation();
  const [activeStep, setActiveStep] = useState(0);
  const steps = ['General product info', 'Price info'];

  const RECURRING_PRICE_INTERVALS = [
    {
      label: t('productPrices.intervals.day'),
      value: PhysicalProductPriceInterval.DAY,
    },
    {
      label: t('productPrices.intervals.week'),
      value: PhysicalProductPriceInterval.WEEK,
    },
    {
      label: t('productPrices.intervals.month'),
      value: PhysicalProductPriceInterval.MONTH,
    },
    {
      label: t('productPrices.intervals.year'),
      value: PhysicalProductPriceInterval.YEAR,
    },
  ];

  const formik = useFormik<CreatePhysicalProductRequest>({
    initialValues: {
      internal_name: '',
      title: '',
      subtitle: '',
      cta: '',
      sku: '',
      description: '',
      image_url: '',
      thumbnail_image_url: '',
      currency: 'usd', // Hardcoded currency for now, later we can add a selector for this
      recurring: undefined,
      type: 'one_time',
      unit_amount: 0,
      platform: 'web',
      is_active: false,
      countries: ['US'],
      number_of_strips_included: 1,
    },
    onSubmit: (values) => {
      onSave(values);
    },
    validationSchema: Yup.object().shape({
      internal_name: Yup.string().required('Internal name is required'),
      title: Yup.string().required('Title is required'),
      cta: Yup.string().required('Cta is required'),
      sku: Yup.string().required('Sku is required'),
      number_of_strips_included: Yup.number()
        .min(1, 'Minimum number of strips included is 1')
        .required('Sku is required'),
    }),
  });

  useEffect(() => {
    if (!isOpen) {
      formik.resetForm();
      setActiveStep(0);
    }
    // eslint-disable-next-line
  }, [isOpen]);

  const handleSuccessfulImageUpload = ({
    image_path,
    resized_image_path,
  }: {
    image_path: string;
    resized_image_path: string;
  }) => {
    formik.getFieldHelpers('image_url').setValue(image_path);
    formik.getFieldHelpers('thumbnail_image_url').setValue(resized_image_path);
  };

  const handleNextClick = () => {
    setActiveStep((step) => step + 1);
  };

  const handleBackClick = () => {
    setActiveStep((step) => step - 1);
  };

  const handlePlatformSelect = (platform: 'web' | 'mobile') => {
    formik.setFieldValue('platform', platform);
  };

  const handlePlatformType = (type: Stripe.Price.Type) => {
    formik.setFieldValue('type', type);
  };

  const handleOnCancel = () => {
    onCancel();
    setActiveStep(0);
    formik.resetForm();
  };

  const areRequiredFieldsProvided =
    !isEmpty(formik.values.internal_name) &&
    !isEmpty(formik.values.title) &&
    !isEmpty(formik.values.cta) &&
    !isEmpty(formik.values.sku);

  return (
    <StyledDialog open={isOpen} onClose={handleOnCancel}>
      <FormikProvider value={formik}>
        <StyledDialogTitle>
          {t('physicalProducts.dialogs.createPhysicalProductTitle')}
        </StyledDialogTitle>
        <StyledStepper activeStep={activeStep}>
          {steps.map((label) => {
            const stepProps: { completed?: boolean } = {};

            return (
              <Step key={label} {...stepProps}>
                <StyledStepLabel>{label}</StyledStepLabel>
              </Step>
            );
          })}
        </StyledStepper>
        {activeStep === 0 && (
          <form>
            <StyledDialogContent>
              <StyledTextField
                type="text"
                fullWidth
                autoComplete="off"
                label={t('physicalProducts.fields.internalName')}
                error={
                  formik.touched.internal_name && !!formik.errors.internal_name
                }
                helperText={
                  formik.errors.internal_name &&
                  formik.touched.internal_name &&
                  formik.errors.internal_name
                }
                {...formik.getFieldProps('internal_name')}
              />

              <StyledTextField
                type="text"
                fullWidth
                autoComplete="off"
                label={t('physicalProducts.fields.title')}
                error={formik.touched.title && !!formik.errors.title}
                helperText={
                  formik.errors.title &&
                  formik.touched.title &&
                  formik.errors.title
                }
                {...formik.getFieldProps('title')}
              />

              <StyledTextField
                type="text"
                fullWidth
                autoComplete="off"
                label={t('physicalProducts.fields.subtitle')}
                error={formik.touched.subtitle && !!formik.errors.subtitle}
                helperText={
                  formik.errors.subtitle &&
                  formik.touched.subtitle &&
                  formik.errors.subtitle
                }
                {...formik.getFieldProps('subtitle')}
              />

              <StyledTextField
                type="text"
                fullWidth
                autoComplete="off"
                label={t('physicalProducts.fields.cta')}
                error={formik.touched.cta && !!formik.errors.cta}
                helperText={
                  formik.errors.cta && formik.touched.cta && formik.errors.cta
                }
                {...formik.getFieldProps('cta')}
              />

              <StyledTextField
                type="text"
                fullWidth
                autoComplete="off"
                label={t('physicalProducts.fields.sku')}
                error={formik.touched.sku && !!formik.errors.sku}
                helperText={
                  formik.errors.sku && formik.touched.sku && formik.errors.sku
                }
                {...formik.getFieldProps('sku')}
              />

              <StyledTextField
                type="number"
                fullWidth
                autoComplete="off"
                label={t('physicalProducts.fields.numberOfStripsIncluded')}
                error={
                  formik.touched.number_of_strips_included &&
                  !!formik.errors.number_of_strips_included
                }
                helperText={
                  formik.errors.number_of_strips_included &&
                  formik.touched.number_of_strips_included &&
                  formik.errors.number_of_strips_included
                }
                {...formik.getFieldProps('number_of_strips_included')}
              />

              <Field type="text" name="description">
                {({ field }: FieldProps) => (
                  <StyledOvQuillEditor
                    value={field.value}
                    handleChange={field.onChange(field.name)}
                    placeholder={t('physicalProducts.fields.description')}
                  />
                )}
              </Field>

              <StyledSwitchContainer>
                <FormControlLabel
                  checked={formik.values.is_active}
                  onChange={() =>
                    formik.setFieldValue('is_active', !formik.values.is_active)
                  }
                  control={<StyledSwitch />}
                  label={t('physicalProducts.fields.isActive')}
                  labelPlacement="top"
                  style={{
                    marginLeft: '0',
                  }}
                />
              </StyledSwitchContainer>

              <StyledText>{t('physicalProducts.fields.platform')}</StyledText>
              <OvSelectorWrapper>
                <OvProductPlatformSelector
                  platform={formik.values.platform}
                  onSelect={handlePlatformSelect}
                />
              </OvSelectorWrapper>

              <OvImageUpload
                imageBasePath={PRODUCT_IMAGES_PATH}
                imageUrl={formik.values.image_url}
                ctaLabel={t('imageUpload.uploadImage')}
                dialogTitle={t('physicalProducts.dialogs.imageUploadTitle')}
                hintText={t('physicalProducts.dialogs.imageUploadHint')}
                handleChange={handleSuccessfulImageUpload}
                resizeOptions={{
                  maxWidth: 250,
                  maxHeight: 250,
                  quailty: 1,
                  resizedImagePath: PRODUCT_THUMBNAIL_IMAGES_PATH,
                }}
              />

              <StyledDialogActions>
                <StyledLightOvButton onClick={handleOnCancel}>
                  {t('common.actions.cancel')}
                </StyledLightOvButton>
                <StyledOvButton
                  onClick={handleNextClick}
                  disabled={!areRequiredFieldsProvided}
                >
                  {t('common.actions.next')}
                </StyledOvButton>
              </StyledDialogActions>
            </StyledDialogContent>
          </form>
        )}

        {activeStep === 1 && (
          <form onSubmit={formik.handleSubmit}>
            <StyledDialogContent>
              <StyledTextField
                type="number"
                fullWidth
                autoComplete="off"
                label={t('physicalProducts.fields.unitAmount')}
                error={
                  formik.touched.unit_amount && !!formik.errors.unit_amount
                }
                helperText={
                  formik.errors.unit_amount &&
                  formik.touched.unit_amount &&
                  formik.errors.unit_amount
                }
                {...formik.getFieldProps('unit_amount')}
              />

              <StyledTextField
                type="text"
                fullWidth
                disabled
                autoComplete="off"
                label={t('physicalProducts.fields.displayPrice')}
                value={formatPrice(formik.values.unit_amount)}
              />

              <StyledText>
                {t('shippingRates.dialogs.targetCountries')}
              </StyledText>
              <StyledOvSelect
                displayEmpty
                {...formik.getFieldProps('countries')}
                multiple
              >
                {getCountriesSelectOptions().map((option) => (
                  <StyledMenuItem key={option.value} value={option.value}>
                    {option.label}
                  </StyledMenuItem>
                ))}
              </StyledOvSelect>

              <OvSelectorWrapper>
                <OvProductTypeSelector
                  type={formik.values.type}
                  onSelect={handlePlatformType}
                />
              </OvSelectorWrapper>

              {formik.values.type === 'recurring' && (
                <>
                  <StyledOvSelect
                    displayEmpty
                    {...formik.getFieldProps('recurring.interval')}
                  >
                    <StyledMenuItem key={'not-selected'} value={undefined}>
                      {t('common.actions.notSelected')}
                    </StyledMenuItem>
                    {RECURRING_PRICE_INTERVALS.map((interval) => (
                      <StyledMenuItem
                        key={interval.value}
                        value={interval.value}
                      >
                        {interval.label}
                      </StyledMenuItem>
                    ))}
                  </StyledOvSelect>

                  <StyledTextField
                    type="number"
                    fullWidth
                    autoComplete="off"
                    label={t('physicalProducts.fields.recurringIntervalCount')}
                    error={
                      formik.touched.recurring && !!formik.errors.recurring
                    }
                    helperText={
                      formik.errors.recurring &&
                      formik.touched.recurring &&
                      formik.errors.recurring
                    }
                    {...formik.getFieldProps('recurring.interval_count')}
                  />
                </>
              )}
              <StyledDialogActions>
                <StyledLightOvButton onClick={handleOnCancel}>
                  {t('common.actions.cancel')}
                </StyledLightOvButton>
                <StyledLightOvButton onClick={handleBackClick}>
                  {t('common.actions.back')}
                </StyledLightOvButton>
                <StyledOvButton type="submit">
                  {t('common.actions.create')}
                </StyledOvButton>
              </StyledDialogActions>
            </StyledDialogContent>
          </form>
        )}
      </FormikProvider>
    </StyledDialog>
  );
};

export default OvCreatePhysicalProductDialog;

const StyledDialog = styled(Dialog)`
  && {
    .MuiDialog-paperScrollPaper {
      max-width: 100%;
      background-color: ${Colours.OV_WHITE};
      overflow: auto;
    }
  }
`;

const StyledDialogTitle = styled(DialogTitle)`
  && {
    text-align: center;
    font-weight: bold;
    text-transform: none;
  }
`;

const StyledDialogContent = styled(DialogContent)`
  && {
    width: 100%;
    min-width: 31.25rem !important;
    padding: 0.5rem 1.5rem !important;
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    gap: 0.75rem;

    @media (min-width: ${breakpoints.sm}) {
      width: 34rem;
    }
  }
`;

const StyledTextField = styled(OvTextField)`
  && {
    flex: 1 0 100%;

    .MuiFormHelperText-root.Mui-error {
      background: ${Colours.OV_WHITE};
      margin: 0;
      padding: 0.25rem 0.75rem;
    }
    .MuiInputLabel-formControl {
      top: -0.2rem;
      opacity: 0.6;
    }
  }
`;

const StyledOvQuillEditor = styled(OvQuillEditor)`
  margin-bottom: 0.75rem;
`;

const StyledStepper = styled(Stepper)`
  && {
    padding: 1rem;
  }
`;

const StyledStepLabel = styled(StepLabel)`
  && {
    .Mui-active {
      color: ${Colours.OV_BASE};
    }

    .Mui-completed {
      color: ${Colours.OV_GREEN};
    }
  }
`;

const StyledDialogActions = styled(DialogActions)`
  && {
    margin-top: 2rem;
    padding: 0 0 0.5rem 1.5rem;
  }
`;

const StyledLightOvButton = styled(OvButton)`
  && {
    border-radius: ${Variables.borderRadius.CLINIC_DASHBOARD_LARGE};
    padding: 0 1rem;
    background-color: ${Colours.OV_WHITE};
    border: 1px solid rgba(1, 39, 70, 0.5);
    color: ${Colours.OV_BASE};
    font-weight: bold;
    text-transform: none;
    max-width: fit-content;
    &:hover {
      border: 1px solid ${Colours.OV_BASE};
      background-color: rgba(1, 39, 70, 0.04);
    }
  }
`;

const StyledOvButton = styled(OvButton)`
  && {
    padding: 0 1rem;
    border-radius: ${Variables.borderRadius.CLINIC_DASHBOARD_LARGE};
    margin-right: 0.75rem;
    transition: none;
    font-weight: bold;
    text-transform: none;
    margin-left: 1rem;
    max-width: fit-content;

    &:last-child {
      margin-right: 0;
    }
  }
`;

const StyledText = styled.p`
  margin-top: 0.5rem;
  font-size: ${Variables.fontSizes.MEDIUM};
  font-weight: bold;
`;

const OvSelectorWrapper = styled.div`
  margin-bottom: 1rem;
  display: block;
`;

const StyledOvSelect = styled(OvSelect)`
  && {
    margin-bottom: 1rem;
    max-width: none;
    height: 3rem;

    &.MuiInputBase-root {
      font-size: 0.875rem;
      border-color: ${Colours.OV_LIGHT_GRAY};
      width: 100%;

      .MuiOutlinedInput-notchedOutline {
        border-color: ${Colours.OV_BASE};
      }

      .MuiSelect-select {
        height: 100%;
        display: flex;
        align-items: center;
      }
    }
  }
`;

const StyledMenuItem = styled(MenuItem)`
  && {
    &.MuiButtonBase-root {
      display: flex;
      justify-content: flex-start;
      padding: 0.375rem 1rem;
      font-size: ${Variables.fontSizes.MEDIUM};
    }
  }
`;

const StyledSwitchContainer = styled(Box)`
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin: 0;
  padding: 0 1rem 1rem 0.5rem;
`;

const StyledSwitch = styled(Switch)`
  && {
    .MuiSwitch-thumb {
      color: ${Colours.OV_BASE};
    }
  }
`;
